/**
 * Save Modal Module
 *
 * Handles saving a coordinate to the user's collection:
 * 1. Auth check before showing modal
 * 2. Display current coordinates
 * 3. Capture full rendering config on save
 */
import { getMapInstance } from "./index";
import { addParcel } from "./db";
import { store, actions } from "./store";
import { formatCoordinate, formatZoom } from "./formatters";
import { trackEvent } from "./analytics";

export function initBuyModal(): void {
  const buyBtn = document.getElementById("buyBtn");
  const buyModal = document.getElementById("buyModal") as HTMLDialogElement | null;
  const loginModal = document.getElementById("loginModal") as HTMLDialogElement | null;
  const buyCancel = document.getElementById("buyCancel");
  const buyConfirm = document.getElementById("buyConfirm");

  // Coord display elements
  const coordRe = document.getElementById("buyCoordRe");
  const coordIm = document.getElementById("buyCoordIm");
  const coordZoom = document.getElementById("buyCoordZoom");

  const parcelNameInput = document.getElementById("buyParcelName") as HTMLInputElement | null;

  if (!buyBtn || !buyModal) return;

  buyBtn.addEventListener("click", () => {
    // Check authentication first
    if (!store.getState().isAuthenticated) {
      if (loginModal) {
        loginModal.showModal();
      }
      return;
    }

    // Get current map state
    const map = getMapInstance();
    if (!map?.config) {
      console.error("Map not initialized");
      return;
    }

    const config = map.config;

    // Update modal with current coordinates
    if (coordRe) coordRe.textContent = formatCoordinate(config.re);
    if (coordIm) coordIm.textContent = formatCoordinate(config.im);
    if (coordZoom) coordZoom.textContent = formatZoom(config.zoom);

    // Clear name input for fresh entry
    if (parcelNameInput) parcelNameInput.value = "";

    buyModal.showModal();

    // Focus name input for quick naming
    if (parcelNameInput) parcelNameInput.focus();
  });

  buyCancel?.addEventListener("click", () => {
    buyModal.close();
  });

  buyConfirm?.addEventListener("click", async () => {
    const map = getMapInstance();
    if (!map?.config) {
      console.error("Map not initialized");
      return;
    }

    const user = store.getState().user;
    if (!user) {
      console.error("User not authenticated");
      buyModal.close();
      return;
    }

    const config = map.config;

    // Disable button during save
    if (buyConfirm) {
      buyConfirm.textContent = "Saving...";
      (buyConfirm as HTMLButtonElement).disabled = true;
    }

    try {
      const parcelName = parcelNameInput?.value.trim() || undefined;

      // Save parcel with full rendering configuration
      const newParcel = await addParcel({
        userId: user.id,
        name: parcelName,

        // Position
        re: config.re,
        im: config.im,
        zoom: config.zoom,

        // Rendering config (for exact visual replication)
        iterations: config.iterations,
        colorScheme: config.colorScheme,
        reverseColors: config.reverseColors,
        smoothColoring: config.smoothColoring,
        paletteMinIter: config.paletteMinIter,
        paletteMaxIter: config.paletteMaxIter,
        scaleWithIterations: config.scaleWithIterations,
        shiftHueAmount: config.shiftHueAmount,
        saturateAmount: config.saturateAmount,
        lightenAmount: config.lightenAmount,
        colorSpace: config.colorSpace,
      });

      if (newParcel) {
        // Update store (triggers collection re-render)
        actions.addParcel(newParcel);
        buyModal.close();
        showSaveSuccess();
        trackEvent("save_parcel", { parcelId: newParcel.id });
      } else {
        console.error("Failed to save parcel");
        alert("Failed to save parcel. Please try again.");
      }
    } catch (error) {
      console.error("Error saving parcel:", error);
      alert("An error occurred. Please try again.");
    } finally {
      // Re-enable button
      if (buyConfirm) {
        buyConfirm.textContent = "Save";
        (buyConfirm as HTMLButtonElement).disabled = false;
      }
    }
  });

  // Close on backdrop click
  buyModal.addEventListener("click", (e) => {
    if (e.target === buyModal) {
      buyModal.close();
    }
  });
}

/**
 * Show a brief success message after saving
 */
function showSaveSuccess(): void {
  const toast = document.createElement("div");
  toast.className = "purchase-toast";
  toast.textContent = "Saved! View it in your Collection.";
  document.body.appendChild(toast);

  requestAnimationFrame(() => {
    toast.classList.add("visible");
  });

  setTimeout(() => {
    toast.classList.remove("visible");
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}
